import Box from "@/components/Box";
import CircleButton from "@/components/CircleButton";
import IconType from "@/components/IconType";
import { TOGGLE_TEST_PRODUCT_FAVOURITE_MUTATION } from "@/graphql/accounts";
import useUserProfile from "@/hooks/useUserProfile";
import LifeStyleIcon from "@/images/LifeStyleIcon";
import PractitionerFavourite from "@/images/PractitionerFavourite";
import Badge from "@/tpo/Badge";
import Group from "@/tpo/Group";
import Stack from "@/tpo/Stack";
import { ActionIcon } from "@/v2/Buttons";
import { useMutation } from "@apollo/client";
import styled, { useTheme } from "styled-components";

import { SHOP_BREAKPOINTS } from "./shopTheme";

const headerActionButtonSize = {
  width: [44, 44, 48],
  height: [44, 44, 48],
  minWidth: [44, 44, 48],
  minHeight: [44, 44, 48]
};

const headerActionButtonStyle = {
  bg: "dark"
};

function HeaderActions({ testProductId, isPractitionerFavourite }) {
  const theme = useTheme();
  const { patientProfile } = useUserProfile();
  const [toggleFavouriteMutation] = useMutation(TOGGLE_TEST_PRODUCT_FAVOURITE_MUTATION);

  const isFavourite = patientProfile?.testProductFavouriteIds?.includes(testProductId);

  return (
    <Group gap={8} alignItems="center">
      {isPractitionerFavourite ? (
        <Box {...headerActionButtonSize} flexShrink={0}>
          <PractitionerFavourite width="100%" height="100%" />
        </Box>
      ) : null}
      <ActionIcon
        {...headerActionButtonStyle}
        variant="circle"
        sx={headerActionButtonSize}
        onClick={e => {
          e.stopPropagation();
          toggleFavouriteMutation({
            variables: { testProductId }
          });
        }}
      >
        <LifeStyleIcon
          size={20}
          outline={!isFavourite}
          filled={isFavourite}
          color={isFavourite ? theme.colors.genetics : "white"}
        />
      </ActionIcon>
      <CircleButton {...headerActionButtonStyle} arrowColor="white" sx={headerActionButtonSize} />
    </Group>
  );
}

function CategoryBadges({ content, categories }) {
  return (
    <Group flexWrap="wrap" gap={8}>
      {content.testType && (
        <Badge bg="purple" color="white" size="xs">
          {content.testType}
        </Badge>
      )}
      {categories.map(cat => (
        <Badge bg="haze" color="dark" size="xs" key={cat.name}>
          {cat.name}
        </Badge>
      ))}
    </Group>
  );
}

const productImageColumnProps = {
  width: "100%",
  minHeight: 319
};

/**
 * Full-width product image below 1152px. Near 1000px, 319px is too short for a
 * 1920×1400 image with background-size: cover.
 */
const ProductImageFrame = styled(Box)`
  @media screen and (min-width: 768px) and (max-width: calc(${SHOP_BREAKPOINTS.sm} - 1px)) {
    min-height: 480px;
  }
`;

function ProductImagePlaceholder() {
  return (
    <ProductImageFrame
      {...productImageColumnProps}
      borderRadius={12}
      bg="white"
      border="1px solid"
      borderColor="dark"
      opacity={0.35}
      aria-hidden
    />
  );
}

function ProductImage({ imageUrl, alt }) {
  if (!imageUrl) {
    return <ProductImagePlaceholder />;
  }

  return (
    <ProductImageFrame
      {...productImageColumnProps}
      borderRadius={12}
      bg="haze"
      role="img"
      aria-label={alt}
      style={{
        backgroundImage: `url(${imageUrl})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: "cover"
      }}
    />
  );
}

function LowerSection({ content, categories, priceSection, laboratory, productName }) {
  return (
    <Box
      display="grid"
      gridTemplateColumns={["1fr", "1fr 1fr", "1fr 1fr"]}
      gridColumnGap={["20px", "20px", "40px"]}
      gridRowGap={["20px", "20px", "32px"]}
      width="100%"
      alignItems={["start", "stretch", "stretch"]}
    >
      <ProductImage imageUrl={laboratory?.productImageUrl} alt={productName} />
      <Stack
        minWidth={0}
        width="100%"
        py={16}
        gap={[16, 0, 0]}
        justifyContent={["flex-start", "space-between", "space-between"]}
      >
        <Stack gap={16}>
          {content.shortDescription && (
            <Box fontSize={14} fontFamily="gilroyMedium">
              {content.shortDescription}
            </Box>
          )}
          <CategoryBadges content={content} categories={categories} />
        </Stack>
        <Box width="100%">{priceSection}</Box>
      </Stack>
    </Box>
  );
}

/** Top / horizontal / bottom padding (mobile, tablet, desktop). Bottom is 96px on all breakpoints. */
const featuredCardPadding = {
  pt: [24, 32, 40],
  pb: 96,
  px: [20, 32, 40]
};

/** Product title: 24px mobile/tablet, 28px desktop (shop breakpoints). */
const featuredProductNameFontSize = [24, 24, 28];

/** Laboratory name: 16px mobile/tablet, 18px desktop (shop breakpoints). */
const featuredLaboratoryNameFontSize = [16, 16, 18];

/** Space between header row and content below (mobile/tablet, desktop). */
const featuredHeaderToContentGap = [20, 20, 28];

const featuredProductIconSize = 60;

function GridLayout({ testProduct, priceSection, onClick }) {
  const { content } = testProduct;
  const categories = content?.categories || [];

  return (
    <Stack
      {...featuredCardPadding}
      gap={featuredHeaderToContentGap}
      onClick={onClick}
      cursor="pointer"
      data-component-name="FeaturedTestProductCard"
    >
      <Stack gap={16}>
        <Group justifyContent="space-between">
          <IconType iconType={content.iconType} iconSize={featuredProductIconSize} />
          <HeaderActions
            testProductId={testProduct.id}
            isPractitionerFavourite={testProduct.isPractitionerFavourite}
          />
        </Group>
        <Box>
          {!!testProduct.name && (
            <Box fontFamily="gilroyBold" lineHeight="36px" fontSize={featuredProductNameFontSize}>
              {testProduct.name}
            </Box>
          )}
          {!!testProduct.laboratory?.name && (
            <Box
              color="anchorBlue"
              fontFamily="gilroyMedium"
              fontSize={featuredLaboratoryNameFontSize}
            >
              {testProduct.laboratory.name}
            </Box>
          )}
        </Box>
      </Stack>
      <LowerSection
        content={content}
        categories={categories}
        priceSection={priceSection}
        laboratory={testProduct.laboratory}
        productName={testProduct.name}
      />
    </Stack>
  );
}

function StackedLayout({ testProduct, priceSection, onClick }) {
  const { content } = testProduct;
  const categories = content?.categories || [];

  return (
    <Stack
      {...featuredCardPadding}
      gap={featuredHeaderToContentGap}
      onClick={onClick}
      cursor="pointer"
      data-component-name="FeaturedTestProductCard"
    >
      <Group justifyContent="space-between" alignItems="flex-start">
        <Group gap={20} alignItems="center">
          <IconType iconType={content.iconType} iconSize={featuredProductIconSize} />
          <Stack gap={0}>
            <Box fontFamily="gilroyBold" lineHeight="36px" fontSize={featuredProductNameFontSize}>
              {testProduct.name}
            </Box>
            <Group alignItems="center" gap={10} flexWrap="wrap">
              {!!testProduct.laboratory?.name && (
                <Box
                  color="anchorBlue"
                  fontFamily="gilroyMedium"
                  fontSize={featuredLaboratoryNameFontSize}
                >
                  {testProduct.laboratory.name}
                </Box>
              )}
              {testProduct.addons?.length > 0 && (
                <Badge bg="dark" color="white" size="xs">
                  add-ons available
                </Badge>
              )}
              {content.centrifuge && (
                <Badge bg="red" color="white" size="xs">
                  centrifuge required
                </Badge>
              )}
            </Group>
          </Stack>
        </Group>
        <HeaderActions
          testProductId={testProduct.id}
          isPractitionerFavourite={testProduct.isPractitionerFavourite}
        />
      </Group>
      <LowerSection
        content={content}
        categories={categories}
        priceSection={priceSection}
        laboratory={testProduct.laboratory}
        productName={testProduct.name}
      />
    </Stack>
  );
}

export default function FeaturedTestProductCard({ testProduct, layout, priceSection, onClick }) {
  const props = { testProduct, priceSection, onClick };

  if (layout === "stacked") {
    return <StackedLayout {...props} />;
  }

  return <GridLayout {...props} />;
}
